export type ScanStatus = 'pending' | 'processing' | 'done' | 'error';
export type Severity = 'high' | 'medium' | 'low';
export type FindingType =
  | 'gap'
  | 'strength'
  | 'needs_review'
  | 'classification'
  | 'extraction_warning';
export type FindingReviewStatus =
  | 'suggested'
  | 'needs_review'
  | 'accepted'
  | 'rejected';
export type RegulationScope =
  | 'machinery_directive_2006_42_ec'
  | 'machinery_regulation_2023_1230'
  | 'transition_review'
  | 'personal_protective_equipment_2016_425';
export type RegulationPack =
  | 'low_voltage_2014_35_eu'
  | 'emc_2014_30_eu'
  | 'radio_equipment_2014_53_eu'
  | 'ai_act_2024_1689'
  | 'cra_2024_2847'
  | 'personal_protective_equipment_2016_425'
  | 'construction_products_2024_3110'
  | 'toy_safety_2025_2509';
export type DocumentType =
  | 'machine_product_overview'
  | 'instructions_for_use'
  | 'declaration_of_conformity'
  | 'declaration_of_incorporation'
  | 'risk_assessment'
  | 'drawings_specifications'
  | 'test_evidence'
  | 'standards_list'
  | 'combined_technical_file'
  | 'maintenance_service_information'
  | 'other';

export const REGULATION_SCOPE_OPTIONS: RegulationScope[] = [
  'machinery_directive_2006_42_ec',
  'machinery_regulation_2023_1230',
  'transition_review',
  'personal_protective_equipment_2016_425',
];

export const LOW_VOLTAGE_REGULATION_PACK: RegulationPack =
  'low_voltage_2014_35_eu';

export const REGULATION_PACK_OPTIONS: RegulationPack[] = [
  LOW_VOLTAGE_REGULATION_PACK,
  'emc_2014_30_eu',
  'radio_equipment_2014_53_eu',
  'ai_act_2024_1689',
  'cra_2024_2847',
  'personal_protective_equipment_2016_425',
  'construction_products_2024_3110',
  'toy_safety_2025_2509',
];

export const REGULATION_PACK_LABELS: Record<RegulationPack, string> = {
  low_voltage_2014_35_eu: 'Low Voltage Directive 2014/35/EU',
  emc_2014_30_eu: 'EMC Directive 2014/30/EU',
  radio_equipment_2014_53_eu: 'Radio Equipment Directive 2014/53/EU',
  ai_act_2024_1689: 'EU AI Act — Regulation (EU) 2024/1689',
  cra_2024_2847: 'Cyber Resilience Act — Regulation (EU) 2024/2847',
  personal_protective_equipment_2016_425:
    'Personal Protective Equipment Regulation (EU) 2016/425',
  construction_products_2024_3110:
    'Construction Products Regulation (EU) 2024/3110',
  toy_safety_2025_2509:
    'Toy Safety Regulation (EU) 2025/2509 — 2009/48/EC transition',
};

export const DOCUMENT_TYPE_OPTIONS: DocumentType[] = [
  'machine_product_overview',
  'instructions_for_use',
  'risk_assessment',
  'declaration_of_conformity',
  'declaration_of_incorporation',
  'drawings_specifications',
  'test_evidence',
  'standards_list',
  'combined_technical_file',
  'maintenance_service_information',
  'other',
];

export const REGULATION_SCOPE_LABELS: Record<RegulationScope, string> = {
  machinery_directive_2006_42_ec: 'EU Machinery Directive 2006/42/EC',
  machinery_regulation_2023_1230: 'Regulation (EU) 2023/1230',
  transition_review:
    'Transition review: Directive 2006/42/EC and Regulation (EU) 2023/1230',
  personal_protective_equipment_2016_425:
    'Personal Protective Equipment Regulation (EU) 2016/425',
};

export function regulationScopeLabel(scope?: string | null): string {
  if (scope && scope in REGULATION_SCOPE_LABELS) {
    return REGULATION_SCOPE_LABELS[scope as RegulationScope];
  }
  return REGULATION_SCOPE_LABELS.machinery_directive_2006_42_ec;
}

export interface Risk {
  name: string;
  severity: Severity;
  standard: string;
  fix: string;
}

export interface Finding {
  id?: string;
  scanId?: string;
  projectId?: string;
  evidenceDocumentId?: string;
  checklistItemId?: string;
  type: FindingType;
  severity: Severity;
  title: string;
  requirementReference?: string;
  recommendation?: string;
  evidenceExcerpt?: string;
  sourceLocation?: string;
  confidence: number;
  reviewStatus: FindingReviewStatus;
  approvalState?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Scan {
  id: string;
  userId: string;
  projectId?: string;
  evidenceDocumentId?: string;
  regulationScope?: RegulationScope;
  filename: string;
  fileSize: number;
  status: ScanStatus;
  score: number | null;
  summary?: string;
  risks: Risk[];
  findings?: Finding[];
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl: string;
  provider: string;
  emailVerified: boolean;
  createdAt: string;
}

export interface Project {
  id: string;
  userId: string;
  organizationId: string;
  clientId?: string;
  name: string;
  productType?: string;
  intendedUse?: string;
  regulationScope: RegulationScope;
  regulationPacks: RegulationPack[];
  regulationPackAssessments: RegulationPackAssessment[];
  currentUserRole: ProjectRole;
  reviewStatus: ProjectReviewStatus;
  createdAt: string;
  updatedAt: string;
}

export type ProjectReviewStatus =
  | 'draft'
  | 'review_requested'
  | 'changes_requested'
  | 'ready_for_export';

export type ProjectRole =
  | 'owner'
  | 'admin'
  | 'reviewer'
  | 'contributor'
  | 'read_only';

export interface RegulationPackAssessment {
  regulationPack: RegulationPack;
  result: string;
  rationale: string;
  sourceReference: string;
  inputs: Record<string, boolean>;
  updatedAt: string;
}

export interface ProjectSummary {
  id: string;
  organizationId: string;
  clientId?: string;
  clientName?: string;
  name: string;
  productType?: string;
  regulationScope: RegulationScope;
  latestScanId?: string;
  latestScanStatus?: ScanStatus;
  latestUploadFilename?: string;
  latestUploadAt?: string;
  checklistCompleted: number;
  checklistTotal: number;
  unresolvedGapCount: number;
  openTaskCount: number;
  reviewStatus: ProjectReviewStatus;
  createdAt: string;
  lastUpdatedAt: string;
}

export type OrganizationRole = 'owner' | 'admin' | 'member';

export interface Organization {
  id: string;
  name: string;
  isPersonal: boolean;
  currentRole: OrganizationRole;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  id: string;
  organizationId: string;
  name: string;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationListResponse {
  organizations: Organization[];
  total: number;
}

export interface OrganizationMember {
  organizationId: string;
  userId: string;
  email: string;
  name: string;
  role: OrganizationRole;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationMemberListResponse {
  members: OrganizationMember[];
  total: number;
}

export interface ClientListResponse {
  clients: Client[];
  total: number;
}

export interface DashboardTask extends ProjectTask {
  projectName: string;
  clientName?: string;
}

export interface DashboardExport {
  projectId: string;
  projectName: string;
  format: string;
  createdAt: string;
}

export interface PartnerDashboard {
  projects: ProjectSummary[];
  overdueTasks: DashboardTask[];
  reviewRequests: ProjectSummary[];
  recentExports: DashboardExport[];
  attentionGroups: Array<{
    clientId?: string;
    clientName?: string;
    projects: ProjectSummary[];
  }>;
  activeClients: number;
  attentionProjects: number;
}

export interface ProjectTemplate {
  id: string;
  organizationId: string;
  createdByUserId: string;
  name: string;
  description: string;
  checklistItems: Array<{
    requirementReference: string;
    title: string;
    explanation: string;
    position: number;
  }>;
  evidenceCategories: DocumentType[];
  reportDefaults: Record<string, unknown>;
  taskPresets: Array<{
    title: string;
    description?: string;
    priority: TaskPriority;
  }>;
  guidanceText: string;
  isShared: boolean;
  canManage: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectTemplateListResponse {
  templates: ProjectTemplate[];
  total: number;
}

export interface ReportBranding {
  mode: 'ce_scan' | 'co_branded' | 'white_label';
  logoUrl: string;
  organizationName: string;
  footerText: string;
  preparedBy: string;
  disclaimer: string;
  accentColor: string;
  updatedAt?: string;
}

export type TaskStatus = 'open' | 'in_progress' | 'completed';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskLinkedObjectType =
  | 'checklist_item'
  | 'finding'
  | 'evidence_document';

export interface ProjectTask {
  id: string;
  projectId: string;
  createdByUserId: string;
  createdByName: string;
  assigneeUserId?: string;
  assigneeName?: string;
  status: TaskStatus;
  priority: TaskPriority;
  title: string;
  description?: string;
  dueDate?: string;
  linkedObjectType?: TaskLinkedObjectType;
  linkedObjectId?: string;
  completionNote?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectMember {
  projectId: string;
  userId: string;
  email: string;
  name: string;
  role: ProjectRole;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectMemberListResponse {
  members: ProjectMember[];
  total: number;
}

export interface TaskResponse {
  task: ProjectTask;
}

export interface TaskListResponse {
  tasks: ProjectTask[];
  total: number;
}

export interface ProjectListResponse {
  projects: ProjectSummary[];
  total: number;
}

export type AuditEventType =
  | 'project_created'
  | 'evidence_uploaded'
  | 'evidence_version_uploaded'
  | 'scan_started'
  | 'scan_completed'
  | 'scan_failed'
  | 'checklist_status_changed'
  | 'checklist_evidence_linked'
  | 'checklist_evidence_unlinked'
  | 'project_regulation_scope_changed'
  | 'project_regulation_pack_added'
  | 'regulation_pack_assessed'
  | 'report_exported'
  | 'ai_recommendations_generated'
  | 'task_created'
  | 'task_status_changed'
  | 'task_completed'
  | 'project_review_requested'
  | 'project_changes_requested'
  | 'project_ready_for_export'
  | 'project_review_invalidated'
  | 'project_template_saved'
  | 'project_template_used';

export interface AuditEvent {
  id: string;
  projectId: string;
  actorUserId: string;
  actorName: string;
  eventType: AuditEventType;
  targetType: string;
  targetId: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AuditResponse {
  auditEvents: AuditEvent[];
  total: number;
}

export type DecisionType =
  | 'checklist_status_override'
  | 'finding_review_status'
  | 'approval';

export interface DecisionLog {
  id: string;
  projectId: string;
  actorUserId: string;
  actorName: string;
  decisionType: DecisionType;
  targetType: string;
  targetId: string;
  checklistItemId?: string;
  findingId?: string;
  evidenceDocumentId?: string;
  evidenceDocumentVersionId?: string;
  previousState?: string;
  newState?: string;
  rationale: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface DecisionLogResponse {
  decisionLogs: DecisionLog[];
  total: number;
}

export type ChecklistStatus =
  | 'not_started'
  | 'evidence_found'
  | 'missing'
  | 'needs_review'
  | 'not_applicable'
  | 'accepted';

export interface EvidenceDocument {
  id: string;
  projectId: string;
  uploadedByUserId: string;
  currentVersionNumber: number;
  versionCount: number;
  filename: string;
  fileSize: number;
  contentHash: string;
  contentType: string;
  documentType: DocumentType;
  extractionStatus: string;
  extractedTextChars: number;
  extractionError?: string;
  extractedAt?: string;
  binaryStorage: string;
  createdAt: string;
  updatedAt: string;
  versions?: EvidenceDocumentVersion[];
}

export interface EvidenceDocumentVersion {
  id: string;
  evidenceDocumentId: string;
  projectId: string;
  uploadedByUserId: string;
  versionNumber: number;
  filename: string;
  fileSize: number;
  contentHash: string;
  contentType: string;
  extractionStatus: string;
  extractedTextChars: number;
  extractionError?: string;
  extractedAt?: string;
  binaryStorage: string;
  note?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EvidenceTextDiffSegment {
  type: 'unchanged' | 'added' | 'removed';
  text: string;
  fromLine?: number;
  toLine?: number;
}

export interface EvidenceVersionComparison {
  projectId: string;
  evidenceDocumentId: string;
  fromVersion: EvidenceDocumentVersion;
  toVersion: EvidenceDocumentVersion;
  hasChanges: boolean;
  diff: EvidenceTextDiffSegment[];
  reviewChecklistItems: ChecklistItem[];
  reviewChecklistItemCount: number;
}

export interface EvidenceResponse {
  evidenceDocuments: EvidenceDocument[];
  total: number;
}

export interface ChecklistItem {
  id: string;
  projectId: string;
  requirementReference: string;
  title: string;
  explanation: string;
  status: ChecklistStatus;
  statusRationale?: string;
  approvalState?: string;
  position: number;
  ownerUserId: string;
  linkedEvidenceDocuments: EvidenceDocument[];
  suggestedEvidenceDocuments?: EvidenceDocument[];
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistResponse {
  checklistItems: ChecklistItem[];
  total: number;
}

export interface ChecklistGapStatusCounts {
  notStarted: number;
  evidenceFound: number;
  missing: number;
  needsReview: number;
  notApplicable: number;
  accepted: number;
}

export interface ChecklistGapDetectionResult {
  projectId: string;
  checklistItems: ChecklistItem[];
  updated: number;
  counts: ChecklistGapStatusCounts;
}

// Sample data for instant demo — no API key required
export const DEMO_SCAN: Scan = {
  id: 'demo-0001',
  userId: 'demo',
  projectId: 'demo-project-0001',
  evidenceDocumentId: 'demo-evidence-0001',
  regulationScope: 'machinery_directive_2006_42_ec',
  filename: 'Hydraulic_Press_TechFile_v3.pdf',
  fileSize: 2_847_000,
  status: 'done',
  score: 67,
  summary:
    'The document contains useful operating and safety content, but it still leaves some important guarding and risk-assessment gaps unresolved. Emergency stop coverage and formal technical-file completeness should be strengthened before relying on it as strong conformity evidence.',
  risks: [
    {
      name: 'Inadequate guard interlocking',
      severity: 'high',
      standard: 'EN ISO 14120:2015 §5.3',
      fix: 'Install electro-mechanical interlocking devices on all access panels with safety category PLd',
    },
    {
      name: 'Pinch point not guarded — feed zone',
      severity: 'high',
      standard: 'EN ISO 13857:2019 Table 1',
      fix: 'Add fixed guard or light curtain at 180mm from pinch point per safety distance tables',
    },
    {
      name: 'Risk assessment missing EN ISO 12100 methodology',
      severity: 'high',
      standard: 'EN ISO 12100:2010 §5',
      fix: 'Redo risk assessment explicitly following the iterative 3-step method; document hazard list',
    },
    {
      name: 'Emergency stop not on all operator positions',
      severity: 'medium',
      standard: 'EN ISO 13850:2015 §4.1.1',
      fix: 'Add mushroom-head E-stop at rear operator station; ensure all stops are category 0 or 1',
    },
    {
      name: 'Operating instructions lack noise emission data',
      severity: 'medium',
      standard: 'Directive 2006/42/EC Annex I §1.7.4.2(u)',
      fix: 'Measure and declare A-weighted sound pressure level at workstations; include in manual',
    },
    {
      name: 'Safety function control circuit below PLc',
      severity: 'medium',
      standard: 'EN ISO 13849-1:2015 §4',
      fix: 'Redesign safety control circuit to achieve PLc minimum; validate with SISTEMA software',
    },
    {
      name: 'CE marking label missing Notified Body number',
      severity: 'low',
      standard: 'Directive 2006/42/EC Annex III',
      fix: 'Add NB identification number (e.g. 0123) next to CE marking if third-party conformity used',
    },
    {
      name: 'Technical file index incomplete',
      severity: 'low',
      standard: 'Directive 2006/42/EC Annex VII §A',
      fix: 'Add assembly drawings with dimensions and BOM with part numbers to technical file index',
    },
  ],
  createdAt: new Date(Date.now() - 3_600_000).toISOString(),
  updatedAt: new Date(Date.now() - 3_590_000).toISOString(),
};

export const DEMO_HISTORY: Scan[] = [
  DEMO_SCAN,
  {
    id: 'demo-0002',
    userId: 'demo',
    projectId: 'demo-project-0002',
    evidenceDocumentId: 'demo-evidence-0002',
    regulationScope: 'machinery_regulation_2023_1230',
    filename: 'ConveyorBelt_CE_Declaration.pdf',
    fileSize: 1_240_000,
    status: 'done',
    score: 82,
    summary:
      'This declaration pack is relatively strong, but a few machine-protection and ergonomics points still need attention. The main remaining weaknesses are around physical guarding and supporting assessments.',
    risks: [
      {
        name: 'Conveyor nip point guards missing',
        severity: 'high',
        standard: 'EN ISO 14120:2015',
        fix: 'Install fixed guards at all nip points with max 6mm opening',
      },
      {
        name: 'Manual handling assessment absent',
        severity: 'medium',
        standard: 'Directive 2006/42/EC Annex I §1.1.5',
        fix: 'Conduct ergonomic assessment per EN 1005-3 for all manual loading positions',
      },
    ],
    createdAt: new Date(Date.now() - 86_400_000).toISOString(),
    updatedAt: new Date(Date.now() - 86_390_000).toISOString(),
  },
  {
    id: 'demo-0003',
    userId: 'demo',
    projectId: 'demo-project-0003',
    evidenceDocumentId: 'demo-evidence-0003',
    regulationScope: 'transition_review',
    filename: 'LatheModel_X200_TechSpec.pdf',
    fileSize: 3_900_000,
    status: 'done',
    score: 91,
    summary:
      'The technical file is well documented overall and shows strong conformity maturity. Only a small improvement around the chuck-guard safety category is still advisable.',
    risks: [
      {
        name: 'Chuck guard interlock category insufficient',
        severity: 'low',
        standard: 'EN ISO 13849-1 §4.5',
        fix: 'Upgrade from PL b to PL c — replace reed switch with safety relay module',
      },
    ],
    createdAt: new Date(Date.now() - 172_800_000).toISOString(),
    updatedAt: new Date(Date.now() - 172_790_000).toISOString(),
  },
];
